import JsBarcode from 'jsbarcode';
import JSZip from 'jszip';

export const sanitizeFilenamePart = (value) =>
    String(value || '')
        .trim()
        .replace(/[\\/:*?"<>|]+/g, '')
        .replace(/\s+/g, ' ')
        .trim();

const wrapText = (ctx, text, maxWidth) => {
    const raw = String(text || '').trim();
    if (!raw) return [];
    const words = raw.split(/\s+/);
    const lines = [];
    let current = '';
    words.forEach((word) => {
        const test = current ? `${current} ${word}` : word;
        if (ctx.measureText(test).width > maxWidth && current) {
            lines.push(current);
            current = word;
        } else {
            current = test;
        }
    });
    if (current) lines.push(current);
    return lines.slice(0, 3);
};

export const createBarcodeLabelCanvas = (rawCode, { productName } = {}) => {
    const barcode = (rawCode || '').trim();
    if (!barcode) return null;

    const src = document.createElement('canvas');
    try {
        JsBarcode(src, barcode, {
            format: 'CODE128',
            width: 2,
            height: 100,
            displayValue: false,
            margin: 10,
            background: '#ffffff',
            lineColor: '#000000',
        });
    } catch (e) {
        console.error(e);
        return null;
    }

    const paddingX = 24;
    const paddingTop = 16;
    const paddingBottom = 20;
    const nameToBarcodeGap = 4;
    const barcodeToCodeGap = 10;
    const nameLineHeight = 20;
    const codeLineHeight = 28;
    const minContentWidth = src.width;
    const out = document.createElement('canvas');
    const ctx = out.getContext('2d');

    ctx.font = '600 14px system-ui, sans-serif';
    const nameLines = wrapText(ctx, productName, minContentWidth);
    const nameBlockHeight = nameLines.length ? nameLines.length * nameLineHeight + nameToBarcodeGap : 0;

    out.width = src.width + paddingX * 2;
    out.height = paddingTop + nameBlockHeight + src.height + barcodeToCodeGap + codeLineHeight + paddingBottom;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);

    let y = paddingTop;
    if (nameLines.length) {
        ctx.fillStyle = '#111827';
        ctx.font = '600 14px system-ui, sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        nameLines.forEach((line, i) => {
            ctx.fillText(line, paddingX, y + i * nameLineHeight);
        });
        y += nameBlockHeight;
    }

    ctx.drawImage(src, paddingX, y);
    y += src.height + barcodeToCodeGap;

    ctx.fillStyle = '#111827';
    ctx.font = '600 18px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.direction = 'ltr';
    ctx.fillText(barcode, out.width / 2, y);

    return out;
};

export const getBarcodeLabelFilename = (barcode, { sku, productName } = {}) => {
    const nameParts = [sanitizeFilenamePart(sku), sanitizeFilenamePart(productName)].filter(Boolean);
    return (nameParts.length ? nameParts.join('-') : `barcode-${sanitizeFilenamePart(barcode) || 'label'}`) + '.png';
};

export const downloadBarcodeLabel = (rawCode, { sku, productName } = {}) => {
    const barcode = (rawCode || '').trim();
    if (!barcode) return;
    const canvas = createBarcodeLabelCanvas(barcode, { productName });
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = getBarcodeLabelFilename(barcode, { sku, productName });
    link.href = canvas.toDataURL('image/png');
    link.click();
};

const canvasToBlob = (canvas) =>
    new Promise((resolve) => {
        if (canvas.toBlob) {
            canvas.toBlob((blob) => resolve(blob), 'image/png');
            return;
        }
        const dataUrl = canvas.toDataURL('image/png');
        const byteString = atob(dataUrl.split(',')[1]);
        const mime = 'image/png';
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        resolve(new Blob([ab], { type: mime }));
    });

export const downloadBarcodesZip = async (items = []) => {
    const zip = new JSZip();
    const usedNames = new Set();
    let added = 0;

    for (let i = 0; i < items.length; i++) {
        const item = items[i] || {};
        const barcode = String(item.barcode || '').trim();
        if (!barcode) continue;

        const canvas = createBarcodeLabelCanvas(barcode, { productName: item.productName });
        if (!canvas) continue;

        const blob = await canvasToBlob(canvas);
        if (!blob) continue;

        let filename = getBarcodeLabelFilename(barcode, { sku: item.sku, productName: item.productName });
        if (usedNames.has(filename)) {
            const base = filename.replace(/\.png$/i, '');
            filename = `${base}-${i + 1}.png`;
        }
        usedNames.add(filename);
        zip.file(filename, blob);
        added += 1;
    }

    if (!added) return { added: 0 };

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const link = document.createElement('a');
    link.download = 'product-barcodes.zip';
    link.href = URL.createObjectURL(zipBlob);
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 2000);
    return { added };
};
